import { useEffect, useRef, useState } from "react";
import useAuthStore from "@/store/useAuthStore";
import { FaPlus, FaHome, FaUsers, FaMoneyBillWave, FaCoins } from "react-icons/fa";
import { Box, Autocomplete, TextField } from "@mui/material";
import { useCreditUsage } from "@/hooks/useTenantDashboardService";
import type { UsageReportResponse } from "@/types/creditTypes";
import {
  useActiveTenants,
  useTenantsUsers,
  useTenantsUsedData,
  useTenantsUsageTracker, useAzureCostData,
  useTenantsUsageTrackerById,
} from "@/hooks/useAdminDashboardService";

import TenantLicenseTable from "@/components/TenantTable/TenentTable";
import StatCardGrid from "@/components/StatCardGrid";
import { useNavigate } from "react-router-dom";
import TokenUsageChart from "@/components/TokenUsageChart/TokenUsageChart";
import { AISummaryBubble } from "@/components/AISummaryBubble";
import AzureCostModal from "./AzureCostModal";

import type { TenantItem } from "@/types/tenantService";

interface LicenseData {
  tenant: string;
  assigned: number;
  used: number;
  expiry: string;
  type: string;
  hasStripePlan?: boolean;
  createdAt?: string;
  accountType?: string;
  id: string;
  status: string;
  contactEmail: string;
  token: number;
}

const AddUserCard = () => {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const handleOutsideClick = (event: MouseEvent) => {
    if (
      dropdownRef.current &&
      !dropdownRef.current.contains(event.target as Node) &&
      cardRef.current &&
      !cardRef.current.contains(event.target as Node)
    ) {
      setDropdownOpen(false);
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleOutsideClick);
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, []);

  return (
    <>
      <div className="relative" ref={cardRef}>
        <div className="p-2 rounded-3xl border-2 border-dashed" style={{ borderColor: 'var(--color-border-grey)' }}>
          <div
            onClick={() => setDropdownOpen(true)}
            className="text-black px-8 py-11 rounded-3xl flex flex-col items-center justify-center cursor-pointer border border-dashed hover:shadow-lg transition min-w-[160px]"
            style={{ 
              backgroundColor: 'var(--color-stat-accent)',
              borderColor: 'var(--color-border-grey)'
            }}
          >
            <div className="rounded-full w-7 h-7 flex items-center justify-center mb-2" style={{ backgroundColor: '#000000' }}>
              <FaPlus style={{ color: 'var(--color-stat-accent)' }} className="text-md" />
            </div>
            <span className="text-center font-bold text-lg">Add New User</span>
          </div>
        </div>
      </div>

      {dropdownOpen && (
        <div
          className="fixed inset-0 z-40 bg-opacity-30 backdrop-blur-xs"
          onClick={() => {
            setDropdownOpen(false);
            navigate("");
          }}
        />
      )}

      {dropdownOpen && cardRef.current && (
        <div
          ref={dropdownRef}
          className="absolute z-70 mt-2 bg-surface text-foreground rounded-2xl shadow-lg w-70 overflow-hidden"
          style={{
            top:
              cardRef.current.getBoundingClientRect().bottom +
              window.scrollY +
              8,
            left: cardRef.current.getBoundingClientRect().left + window.scrollX,
            position: "absolute",
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Hiding individual registration from admin panel, users self-register now */}
          {/* 
          <button
            onClick={() => {
              setDropdownOpen(false);
              navigate("/add/individual");
            }}
            className="w-full text-left px-4 py-4 cursor-pointer hover:bg-background text-xl text-foreground"
          >
            Individual
          </button>
          <div className="border-t border-border opacity-40" />
          */}
          <button
            onClick={() => {
              setDropdownOpen(false);
              navigate("/add/company");
            }}
            className="w-full text-left px-4 py-4 cursor-pointer hover:bg-background text-xl text-foreground"
          >
            Company
          </button>
        </div>
      )}
    </>
  );
};

const AdminDashboard = () => {
  const [selectedCompanyId, setSelectedCompanyId] = useState<string>("ALL");
  const [selectedUserId, setSelectedUserId] = useState<string>("ALL");

  const pageSize = 10;
  const page = 1;
  const { data: tenantsData, isError: isTenantError } = useActiveTenants();
  const { data: userData, isError: isUserError } = useTenantsUsers();

  const [chartType, setChartType] = useState<"yearly" | "monthly">("yearly");
  const [chartMonth, setChartMonth] = useState<number>(new Date().getMonth() + 1);

  const trackerParams = chartType === "yearly" 
    ? { type: "yearly" as const, year: new Date().getFullYear() }
    : { type: "monthly" as const, year: new Date().getFullYear(), month: chartMonth };

  const selectedUserObj = ((userData?.data?.items as any[]) || []).find(u => u.id === selectedUserId);
  const chartQueryId = selectedUserId !== "ALL" && selectedUserObj ? selectedUserObj.tenantId : selectedCompanyId;
  const chartQueryUserId = selectedUserId !== "ALL" ? selectedUserId : undefined;

  const { data: allUsageData } = useTenantsUsageTracker(trackerParams);
  const { data: companyUsageData } = useTenantsUsageTrackerById({ 
    id: chartQueryId, 
    userId: chartQueryUserId,
    ...trackerParams 
  });

  const chartDataRaw = (selectedCompanyId === "ALL" && selectedUserId === "ALL") ? allUsageData : companyUsageData;
  const currentChartData = Array.isArray(chartDataRaw) ? chartDataRaw : (chartDataRaw?.data || []);

  const { data: azureCostRes, isLoading: isAzureCostLoading, isError: isAzureCostError, refetch: refetchAzureCost } = useAzureCostData();
  const azureCost = azureCostRes?.data;

  const {
    data: tenantUsageDataRaw,
    isError: isUsageError,
    refetch,
  } = useTenantsUsedData(page, pageSize);

  const [licenseData, setLicenseData] = useState<LicenseData[]>([]);
  console.log("License Data:", licenseData);
  const totalActiveTenants =
    !isTenantError && tenantsData?.data?.totalCount
      ? tenantsData.data.totalCount
      : 0;

  const totalTenantUser =
    !isUserError && userData?.data?.totalCount ? userData.data.totalCount : 0;

  const tenantsUsedItems: TenantItem[] =
    !isUsageError && Array.isArray(tenantUsageDataRaw?.data?.items)
      ? tenantUsageDataRaw?.data.items
      : [];

  useEffect(() => {
    const transformed: LicenseData[] = tenantsUsedItems.filter(item => item.type !== "INDIVIDUAL").map((item) => {
      const activeSub = item.subscriptions?.find((s: any) => s.status === "ACTIVE");
      const subscription = activeSub || item.subscriptions?.[0];
      const usage = item.TenantUsageCounter?.[0];
      const effectivePlan = activeSub?.planMaster || item.planMaster;
      const planName = (effectivePlan?.name || subscription?.plan || "").trim().toUpperCase();
      const hasPurchasedPlan = Boolean(
        (planName && planName !== "FREE" && (activeSub?.status === "ACTIVE" || subscription?.status === "ACTIVE")) ||
        subscription?.stripeSubscriptionId
      );

      return {
        tenant: item.name ?? "N/A",
        assigned: subscription?.licenseCount ?? effectivePlan?.licenselimit ?? 0,
        used: usage?.used ?? 0,
        token: subscription?.tokenCount ?? effectivePlan?.tokenlimit ?? 0,
        expiry: hasPurchasedPlan ? (activeSub?.endDate ?? subscription?.endDate ?? "") : "",
        type: effectivePlan?.displayName || effectivePlan?.name || (hasPurchasedPlan ? "Starter" : "Free"),
        contactEmail: item?.contactEmail ?? "N/A",
        status: item?.status ?? "inactive",
        id: item?.id ?? "unknown",
        hasStripePlan: hasPurchasedPlan,
        createdAt: item?.createdAt ?? "",
        accountType: item?.type ?? "COMPANY",
      };
    });

    setLicenseData(transformed || []);
  }, [tenantsUsedItems]);

  const [isAzureModalOpen, setIsAzureModalOpen] = useState(false);
  const handleOpenAzureModal = () => setIsAzureModalOpen(true);
  const handleCloseAzureModal = () => setIsAzureModalOpen(false);

  const usageArray = Array.isArray(allUsageData) ? allUsageData : (allUsageData?.data || []);
  const totalPlatformTokens = usageArray.reduce((acc: number, item: any) => acc + (item.tokenused || 0), 0) || 0;
  
  const { data: creditUsageData } = useCreditUsage({}, true);
  const { consumptions = [] } = (creditUsageData as unknown as UsageReportResponse) || {};
  const totalCreditsConsumed = consumptions.reduce((acc: number, item: any) => acc + (item.creditsUsed || 0), 0) || 0;
  
  const azureCostDisplay = azureCost ? `${azureCost.totalCost.toFixed(2)} ${azureCost.currency}` : "Loading...";

  const role = useAuthStore((state) => state.role);
  const stats = [
    {
      icon: <FaHome className="text-accent text-xl" />,
      label: "Active Organizations",
      value: totalActiveTenants.toString(),
    },
    {
      icon: <FaUsers className="text-accent text-2xl" />,
      label: "Total Users",
      value: totalTenantUser.toString(),
    },
    ...(role === "SUPER_ADMIN" ? [{
      icon: <FaMoneyBillWave className="text-accent text-xl" />,
      label: "Azure Cost (MTD)",
      value: azureCostDisplay,
      onClick: handleOpenAzureModal,
    }] : []),
    {
      icon: <FaCoins className="text-accent text-xl" />,
      label: "Total Credits Consumed",
      value: totalCreditsConsumed.toLocaleString(),
    }
  ];

  return (
    <>
      <main className="flex-1 p-4 md:p-6 mt-1">
        <div className="bg-surface rounded-lg p-6">
          <StatCardGrid stats={stats} renderExtraCard={AddUserCard} />
        
        </div>

        <div className="mt-6 mb-4 flex items-center justify-between">
          <h2 className="text-xl font-bold">Token Usage Overview</h2>
          <div className="flex gap-4 items-center">
            <select
              value={chartType}
              onChange={(e) => setChartType(e.target.value as "yearly" | "monthly")}
              className="p-2 border rounded-md"
              style={{ borderColor: "var(--color-border-grey)" }}
            >
              <option value="yearly">Yearly View</option>
              <option value="monthly">Monthly View</option>
            </select>

            {chartType === "monthly" && (
              <select
                value={chartMonth}
                onChange={(e) => setChartMonth(Number(e.target.value))}
                className="p-2 border rounded-md"
                style={{ borderColor: "var(--color-border-grey)" }}
              >
                {Array.from({ length: 12 }, (_, i) => (
                  <option key={i + 1} value={i + 1}>
                    {new Date(0, i).toLocaleString('default', { month: 'long' })}
                  </option>
                ))}
              </select>
            )}

            <Autocomplete
              disabled={selectedUserId !== "ALL" && ((userData?.data?.items as any[]) || []).find(u => u.id === selectedUserId)?.tenant?.type === "INDIVIDUAL"}
              options={((tenantsData?.data?.items as any[]) || []).filter(t => t.type !== "INDIVIDUAL")}
              getOptionLabel={(option) => option.name}
              value={((tenantsData?.data?.items as any[]) || []).find(t => t.id === selectedCompanyId && t.type !== "INDIVIDUAL") || null}
              onChange={(_, newValue) => {
                setSelectedCompanyId(newValue ? newValue.id : "ALL");
                setSelectedUserId("ALL");
              }}
              renderInput={(params) => (
                <TextField 
                  {...params} 
                  placeholder="Select Company" 
                  size="small"
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <>
                        <Box sx={{ ml: 1, color: 'var(--color-text-primary)', display: 'flex', alignItems: 'center' }}>
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"></circle><line x1="21" y1="21" x2="16.65" y2="16.65"></line></svg>
                        </Box>
                        {params.InputProps.startAdornment}
                      </>
                    )
                  }}
                />
              )}
              sx={{
                width: 250,
                '& .MuiOutlinedInput-root': {
                  borderRadius: '0.375rem',
                  color: 'var(--color-text-primary)',
                  '& fieldset': {
                    borderColor: 'var(--color-border-grey)',
                  },
                  '&:hover fieldset': {
                    borderColor: 'var(--color-border-grey)',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: 'var(--color-text-primary)',
                  },
                },
                '& .MuiSvgIcon-root': {
                  color: 'var(--color-text-primary)',
                },
                '& .MuiAutocomplete-inputRoot': {
                  padding: '1px !important',
                },
                '& .MuiAutocomplete-input': {
                  padding: '7px 4px !important',
                }
              }}
            />
            <Autocomplete
              options={
                selectedCompanyId !== "ALL"
                  ? ((userData?.data?.items as any[]) || []).filter(u => u.tenantId === selectedCompanyId)
                  : ((userData?.data?.items as any[]) || []).filter(u => u.tenant?.type === "INDIVIDUAL")
              }
              getOptionLabel={(option) => option.name || option.email || "Unknown"}
              value={((userData?.data?.items as any[]) || []).find(u => u.id === selectedUserId) || null}
              onChange={(_, newValue) => {
                setSelectedUserId(newValue ? newValue.id : "ALL");
                if (!newValue || newValue.tenant?.type === "INDIVIDUAL") {
                  setSelectedCompanyId("ALL"); // Reset company selection
                }
              }}
              renderInput={(params) => (
                <TextField 
                  {...params} 
                  placeholder={selectedCompanyId !== "ALL" ? "Select Company User" : "Select Individual"} 
                  size="small"
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <>
                        <Box sx={{ ml: 1, color: 'var(--color-text-primary)', display: 'flex', alignItems: 'center' }}>
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"></circle><line x1="21" y1="21" x2="16.65" y2="16.65"></line></svg>
                        </Box>
                        {params.InputProps.startAdornment}
                      </>
                    )
                  }}
                />
              )}
              sx={{
                width: 250,
                '& .MuiOutlinedInput-root': {
                  borderRadius: '0.375rem',
                  color: 'var(--color-text-primary)',
                  '& fieldset': {
                    borderColor: 'var(--color-border-grey)',
                  },
                  '&:hover fieldset': {
                    borderColor: 'var(--color-border-grey)',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: 'var(--color-text-primary)',
                  },
                },
                '& .MuiSvgIcon-root': {
                  color: 'var(--color-text-primary)',
                },
                '& .MuiAutocomplete-inputRoot': {
                  padding: '1px !important',
                },
                '& .MuiAutocomplete-input': {
                  padding: '7px 4px !important',
                }
              }}
            />
          </div>
        </div>
        <TokenUsageChart data={currentChartData} title={(selectedCompanyId === "ALL" && selectedUserId === "ALL") ? "Platform Token Usage" : "Selected Token Usage"} />

        <div className="mt-6">
          <Box sx={{ overflowX: "auto" }}>
            <TenantLicenseTable
              data={licenseData}
              showHeading={true}
              setShouldFetch={() => refetch()}
            />
          </Box>
        </div>
        <AISummaryBubble 
          pageContext={JSON.stringify({ 
            totalActiveTenants, 
            totalTenantUser, 
            tenantsData: licenseData.slice(0, 20) // send top 20 to avoid token limits
          })} 
          pageType="Admin Dashboard" 
        />

        <AzureCostModal
          open={isAzureModalOpen}
          onClose={handleCloseAzureModal}
          data={azureCost}
          isLoading={isAzureCostLoading}
          isError={isAzureCostError}
          onRefresh={refetchAzureCost}
          totalTokens={totalPlatformTokens}
        />
      </main>
    </>
  );
};

export default AdminDashboard;
